import React, { Fragment, useRef } from 'react';
import ReactTooltip from 'react-tooltip';
import { noop } from 'lodash';
import clsx from 'clsx';

interface ConfirmButtonArrayProps {
  id: string;
  entityName?: string;
  disabled?: boolean;
  children: React.ReactNode;
  onClick: () => void;
  afterShow?: () => void;
  afterHide?: () => void;
  isCapture?: boolean;
  className?: string;
  tooltipOverrides?: any;
  customization?: any;
}

export function ConfirmButtonArray({
  id,
  entityName = '',
  disabled = false,
  children,
  onClick,
  afterShow = noop,
  afterHide = noop,
  isCapture = false,
  className = '',
  ...rest
}: ConfirmButtonArrayProps) {
  const tooltipRef = useRef(null);
  const onCancel = () => {
    hideTooltip();
  };

  const hideTooltip = () => {
    const current: any = tooltipRef.current;
    current!.tooltipRef = null;
    ReactTooltip.hide();
  };

  const onConfirm = () => {
    hideTooltip();
    onClick();
  };

  const {
    offset = { top: -100, left: -50 },
    place = 'left',
    arrowColor = 'transparent',
    isModalConfirm = false
  } = rest.tooltipOverrides || {};

  const { tooltipText, actionButtonLabel = 'Remove it', actionCancelButtonLabel = 'Cancel' } = rest.customization || {};

  return (
    <Fragment>
      <ReactTooltip
        id={id}
        globalEventOff='click'
        isCapture={isCapture}
        afterShow={afterShow}
        afterHide={afterHide}
        effect='solid'
        className='confirmationTooltip'
        type='light'
        multiline
        border={false}
        clickable
        ref={tooltipRef}
        arrowColor={arrowColor}
        place={place}
        offset={offset}
      >
        <div
          className={clsx('p-0 rounded border-primary-500', isModalConfirm ? 'border-b-4' : 'border-t-4')}
          data-testid='tooltip-content'
        >
          <div className='p-4 text-sm font-normal text-gray-700 font-effra text-black-default'>
            <p>
              {!tooltipText
                ? `If you remove this ${entityName} it can't be recovered. Are you sure you want to remove this ${entityName}?`
                : tooltipText}
            </p>
          </div>
          <div className='flex justify-between p-3 border-t-1 border-primary-500 bg-gray-10'>
            <button
              className='h-8 p-2 bg-transparent border-none btn btn-secondary'
              type='button'
              onClick={onCancel}
              data-testid='action-cancel-button'
            >
              {actionCancelButtonLabel}
            </button>
            <button
              className='h-8 p-2 btn btn-secondary focus:outline-none focus-visible:ring'
              type='button'
              onClick={onConfirm}
              data-testid='action-confirm-button'
            >
              {actionButtonLabel}
            </button>
          </div>
        </div>
      </ReactTooltip>
      <button
        type='button'
        disabled={disabled}
        className={clsx('', className)}
        data-event='click'
        data-for={id}
        data-tip=''
        data-testid='confirm-button'
      >
        {children}
      </button>
    </Fragment>
  );
}
